import { BellOutlined, CloseOutlined, LockOutlined, PushpinOutlined } from '@ant-design/icons'
import { useChannels } from '@entities/channel'
import { usePartners } from '@entities/partner'
import { MessageView } from '@entities/post'

import { useLastAppStateStore } from '@app/providers/router/model/store/last-app-state-store'
import { checkDraftPost, savePayload } from '@entities/post/api'
import { kktuCodes } from '@entities/post/model/data/kktuCodes'
import OneContract from '@shared/assets/1-contract.svg'
import TwoContract from '@shared/assets/2-contract.svg'
import { advertisingPostType, collaborationPostType } from '@shared/lib/constants/constants'
import { extractLinksFromHTML } from '@shared/lib/extractLinksFromHTML/extractLinksFromHTML'
import { randomId } from '@shared/lib/randomId/randomId'
import { transformToTreeData } from '@shared/lib/transformToTreeData/transformToTreeData'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { IconSwitch } from '@shared/ui/icon-switch/icon-switch'
import { MobileSwitch } from '@shared/ui/mobile-switch/mobile-switch'
import { useQueryClient } from '@tanstack/react-query'
import type { FormProps } from 'antd'
import {
    Avatar,
    Button,
    Col,
    Divider,
    Flex,
    Form,
    Image,
    Input,
    InputNumber,
    Radio,
    Row,
    Select,
    theme,
    TreeSelect,
    Typography
} from 'antd'
import { Input as AntMobileInput, Popup } from 'antd-mobile'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { usePosts } from '../../model/hooks/use-posts'
import type { AdLink, AdPriceMethod, OrdResourceType, Post, Reallocation } from '../../model/types'
import { MessageType, type Message, type PostState } from '../../model/types'
import * as styles from './edit-ads-post-mobile-modal.module.scss'

const { Text, Link } = Typography

interface EditPostModalProps {
    open: boolean
    onClose: () => void
    editPostData: Post
    sessionId?: string | null
    adCampaignName: string
}

type FieldType = {
    title: string
    state: PostState
    channels: number[]
    messages: Message[]
    publishDate: Dayjs
    publishTime: Dayjs
    notify: boolean
    isPinned: boolean
    autoDeleteHours: number
    keepOnTopHours: number
    isAdvertising: boolean
    adCampaign: number
    adCampaignName: string
    advertisingInfo: {
        budget: number
        viewToken: string
        sellerConfirm: boolean
        customerConfirm: boolean
        paymentConfirmed: boolean
        priceMethod: AdPriceMethod
        adCampaign: number
        links: AdLink[]
        ord: {
            withAgent: boolean
            category: string
            agentCommission: number
            resourceName: string
            resourceUrl: string
            resourceType: OrdResourceType
            reallocations: Reallocation[]
            nds: boolean
            ndsPercent: number
        }
    }
    postType: {
        value: number
    }
}

const BOT_USERNAME = process.env.BOT_USERNAME || ''

export const EditAdsPostMobileModal = ({
    open,
    editPostData,
    sessionId,
    adCampaignName,
    onClose
}: EditPostModalProps) => {
    const {
        token: { colorPrimary }
    } = theme.useToken()

    const { currentChannel } = useChannels()

    const [values, setValues] = useState<FieldType>()

    const {
        actions: { editPost },
        postTypes,
        editPostIsLoading
    } = usePosts({ channelId: values?.channels?.[0] })

    const [form] = Form.useForm<FieldType>()

    const budget = form.getFieldValue('advertisingInfo')?.budget

    const queryClient = useQueryClient()

    const onFinish: FormProps<FieldType>['onFinish'] = useCallback(
        async ({ ...values }: FieldType) => {
            if (!currentChannel) {
                return
            }

            const dateStr = values.publishDate // '2025-06-13'
            const timeStr = values.publishTime // '14:30'

            const [hour, minute] = timeStr.split(':').map(Number)

            const publishDate = dayjs(dateStr).hour(hour).minute(minute).second(0)

            await editPost({
                ...values,
                id: editPostData.id,
                publishDate: publishDate?.toDate(),
                advertisingInfo: {
                    ...values.advertisingInfo,
                    sellerConfirm: false,
                    customerConfirm: false
                }
            })

            queryClient.invalidateQueries({ queryKey: ['ad-posts', editPostData.advertisingInfo?.adCampaign] })

            onClose()
        },
        [currentChannel, editPost, editPostData.advertisingInfo?.adCampaign, editPostData.id, onClose, queryClient]
    )

    const onSubmit = useCallback(
        (state?: Partial<FieldType>) => {
            if (state) {
                form.setFieldsValue({
                    ...state
                })
            }

            form.submit()
        },
        [form]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const { partnersResources } = usePartners({ loadPartnersResources: true })

    const { setState } = useLastAppStateStore(useShallow(({ setState }) => ({ setState })))

    const onEditPostUsingBot = useCallback(async () => {
        const tg = (window as any).Telegram?.WebApp

        tg.showConfirm('Для редактирования содержания поста, необходимо перейти в бот', async (ok?: boolean) => {
            if (!ok) return

            const sessionId = randomId()

            setState({ page: location.pathname, action: 'edit-post', sessionId: sessionId, postId: editPostData.id })

            const payloadId = await savePayload({
                action: 'edit_post',
                session_id: sessionId,
                post_id: editPostData.id,
                platform: 'mobile'
            })

            const url = `https://t.me/${BOT_USERNAME}?start=payload_${payloadId}`

            tg.openTelegramLink(url)
            tg.close()
        })
    }, [editPostData.id, setState])

    useEffect(() => {
        if (!sessionId) return
        checkDraftPost(sessionId).then((postData) => {
            if (postData) {
                form.setFieldsValue({
                    messages: [
                        {
                            ...postData.post,
                            id: Date.now(),
                            file: postData.post.file.map((file) => {
                                const item = {
                                    ...file,
                                    name: file.response.file_name,
                                    type: file.response.file_type,
                                    url: file.response.file_url
                                }
                                if (
                                    file.response.file_type !== MessageType.AUDIO &&
                                    file.response.file_type !== MessageType.VOICE &&
                                    file.response.file_type !== MessageType.DOCUMENT
                                ) {
                                    item.thumbUrl = file.response?.preview_url || file.response.file_url
                                }
                                return item
                            })
                        }
                    ]
                })
            }
        })
    }, [form, sessionId])

    useEffect(() => {
        const links = extractLinksFromHTML(values?.messages[0].text)

        form.setFieldsValue({
            advertisingInfo: {
                links
            }
        })
    }, [form, values?.messages])

    const isOrd = Form.useWatch('isOrd', form)

    const postType =
        editPostData.postType?.name && editPostData.postType?.value
            ? {
                  label: editPostData.postType?.name,
                  value: editPostData.postType?.value
              }
            : undefined

    const navigate = useNavigate()

    return (
        <Popup visible={open} onMaskClick={onClose} onClose={onClose} bodyStyle={{ background: '#F0F3F5' }}>
            <Form
                form={form}
                name="add-post"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                onFinish={onFinish}
                initialValues={{
                    ...editPostData,
                    publishDate: editPostData?.publishDate
                        ? dayjs(editPostData.publishDate).format('YYYY-MM-DD')
                        : undefined,
                    publishTime: editPostData?.publishDate
                        ? dayjs(editPostData.publishDate).format('HH:mm')
                        : undefined,
                    keepOnTopHours: String(editPostData.keepOnTopHours).padStart(2, '0') + ':00',
                    postType,
                    messages: editPostData?.messages.map((item) => ({
                        ...item,
                        file: item.file.map((file) => {
                            const item = {
                                ...file,
                                name: file.response.file_name,
                                type: file.response.file_type,
                                url: file.response.file_url
                            }
                            if (
                                file.response.file_type !== MessageType.AUDIO &&
                                file.response.file_type !== MessageType.VOICE &&
                                file.response.file_type !== MessageType.DOCUMENT
                            ) {
                                item.thumbUrl = file.response?.preview_url || file.response.file_url
                            }
                            return item
                        })
                    })),
                    isOrd: !!editPostData.advertisingInfo?.ordInfo,
                    adCampaignName,
                    advertisingInfo: {
                        ...editPostData.advertisingInfo,
                        ord: editPostData.advertisingInfo?.ordInfo
                    }
                }}
                style={{ padding: '20px 16px', maxHeight: '100vh', overflow: 'auto' }}
                onValuesChange={(_, values) => setValues(values)}
            >
                <Row gutter={[10, 10]}>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Row>
                                <Col span={24} style={{ marginBottom: '24px' }}>
                                    <Flex gap={6}>
                                        <CustomTag>
                                            {budget > 0 ? advertisingPostType.name : collaborationPostType.name}
                                        </CustomTag>
                                        <Form.Item<FieldType>
                                            name="postType"
                                            required={false}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                placeholder="Выберите тип"
                                                labelInValue
                                                optionLabelProp="label"
                                                popupMatchSelectWidth={false}
                                                popupRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <div style={{ padding: '8px', textAlign: 'center' }}>
                                                            <Button
                                                                type="primary"
                                                                onClick={() => navigate('/settings')}
                                                            >
                                                                + Новый тип
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            >
                                                {postTypes?.map((item) => (
                                                    <Select.Option
                                                        style={{ textAlign: 'center' }}
                                                        key={item.id}
                                                        value={item.id}
                                                        label={item.name}
                                                    >
                                                        <CustomTag color={item.color}>{item.name}</CustomTag>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType> name="adCampaignName">
                                        <Input placeholder="Название рекламной компании" size="large" readOnly />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        name="channels"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            style={{ width: '100%' }}
                                            disabled
                                            popupMatchSelectWidth={false}
                                            size="large"
                                        >
                                            {partnersResources?.map((c) => (
                                                <Select.Option value={c.id} key={c.id}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar src={c.photoUrl} size={20} />
                                                        {c.name}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Row gutter={[10, 10]}>
                                <Col span={24}>
                                    <Text style={{ fontSize: '17px', color: colorPrimary, whiteSpace: 'nowrap' }}>
                                        Опубликовать
                                    </Text>
                                    <Flex gap={8} align="center" style={{ position: 'relative', marginTop: '10px' }}>
                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="publishDate"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0, marginLeft: 'auto' }}
                                            >
                                                <AntMobileInput
                                                    type="date"
                                                    placeholder="Дата публикации"
                                                    style={{ width: '120px' }}
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="publishTime"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <AntMobileInput
                                                    type="time"
                                                    placeholder="Время публикации"
                                                    style={{ width: '82px' }}
                                                />
                                            </Form.Item>
                                        </div>
                                        {/* <ArrowDownOutlined className={styles.arrowIcon} /> */}
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Flex
                                        gap={8}
                                        align="center"
                                        justify="space-between"
                                        style={{ position: 'relative' }}
                                    >
                                        <Text style={{ color: colorPrimary, fontSize: '17px', whiteSpace: 'nowrap' }}>
                                            Держать в топе
                                        </Text>
                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="keepOnTopHours"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <AntMobileInput
                                                    type="time"
                                                    placeholder="Время публикации"
                                                    style={{ width: '82px' }}
                                                />
                                            </Form.Item>
                                        </div>
                                        {/* <ArrowDownOutlined className={styles.arrowIcon} /> */}
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Flex gap={8} align="center" justify="space-between">
                                        <Text style={{ color: colorPrimary, fontSize: '17px', whiteSpace: 'nowrap' }}>
                                            Удалить через
                                        </Text>

                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="autoDeleteHours"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    placeholder="Удалить через, ч"
                                                    options={[
                                                        {
                                                            label: '12',
                                                            value: 12
                                                        },
                                                        {
                                                            label: '24',
                                                            value: 24
                                                        },
                                                        { label: '48', value: 48 },
                                                        { label: '72', value: 72 }
                                                    ]}
                                                    allowClear
                                                />
                                            </Form.Item>
                                        </div>
                                    </Flex>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Flex align="center">
                                <Text style={{ fontSize: '17px', color: colorPrimary, whiteSpace: 'nowrap' }}>
                                    Закреп/Уведомления
                                </Text>
                                <Form.Item<FieldType>
                                    name="isPinned"
                                    required={false}
                                    style={{ marginLeft: 'auto', marginBottom: 0 }}
                                >
                                    <IconSwitch text={'Закрепить'}>
                                        <PushpinOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                                <Form.Item<FieldType> name="notify" required={false} style={{ marginBottom: 0 }}>
                                    <IconSwitch text={'Отправить со звуком'}>
                                        <BellOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                            </Flex>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Form.Item name={['advertisingInfo', 'paymentConfirmed']}>
                                <Select
                                    placeholder="Выберите статус оплаты"
                                    allowClear
                                    options={[
                                        { label: 'Оплачен', value: true },
                                        { label: 'Не оплачен', value: false }
                                    ]}
                                    size="large"
                                />
                            </Form.Item>
                            <Form.Item name={['advertisingInfo', 'priceMethod']}>
                                <div className={styles.radioGroup}>
                                    <Radio.Group
                                        options={[
                                            {
                                                label: 'Фикс',
                                                value: 'fix'
                                            },
                                            { label: 'CPM', value: 'cpm' }
                                        ]}
                                        size="large"
                                        optionType="button"
                                    />
                                </div>
                            </Form.Item>
                            <Row gutter={[8, 8]} align={'middle'}>
                                <Col span={16}>
                                    <Form.Item<FieldType>
                                        name={['advertisingInfo', 'budget']}
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            min={0}
                                            style={{ width: '100%' }}
                                            size="large"
                                            placeholder="Введите стоимость"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item<FieldType> name={['advertisingInfo', 'ord', 'nds']}>
                                        <MobileSwitch uncheckedText="Без НДС" checkedText="с НДС" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item<FieldType> name={['advertisingInfo', 'ord', 'ndsPercent']} hidden>
                                <Input />
                            </Form.Item>
                        </div>
                    </Col>
                    {form.getFieldsValue().messages && (
                        <Col span={24}>
                            <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                                {form
                                    .getFieldsValue()
                                    .messages?.map((item) => <MessageView message={item} key={item.id} />)}
                                <Button
                                    type="primary"
                                    size="large"
                                    style={{ width: '100%', marginTop: '10px' }}
                                    onClick={onEditPostUsingBot}
                                >
                                    Редактировать
                                </Button>
                            </div>
                        </Col>
                    )}

                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Form.Item
                                label={<Text style={{ fontSize: '17px', color: colorPrimary }}>Режим ОРД</Text>}
                                name="isOrd"
                                valuePropName="checked"
                                labelAlign="left"
                                labelCol={{ flex: 'auto' }}
                                wrapperCol={{ flex: 'none' }}
                                style={{ marginBottom: 0 }}
                            >
                                <MobileSwitch />
                            </Form.Item>
                            {isOrd && (
                                <>
                                    <Divider style={{ margin: '16px 0' }} />
                                    <Form.Item
                                        name={['advertisingInfo', 'ord', 'withAgent']}
                                        label={
                                            <Text style={{ fontSize: '17px', color: colorPrimary }}>
                                                Выберите тип договора
                                            </Text>
                                        }
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Radio.Group
                                            className={styles.contractRadioGroup}
                                            options={[
                                                {
                                                    value: false,
                                                    label: (
                                                        <Image
                                                            src={OneContract}
                                                            preview={false}
                                                            style={{
                                                                opacity: form.getFieldsValue().advertisingInfo?.ord
                                                                    .withAgent
                                                                    ? 0.6
                                                                    : 1
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    value: true,
                                                    label: (
                                                        <Image
                                                            src={TwoContract}
                                                            preview={false}
                                                            style={{
                                                                opacity: form.getFieldsValue().advertisingInfo?.ord
                                                                    .withAgent
                                                                    ? 1
                                                                    : 0.6
                                                            }}
                                                        />
                                                    )
                                                }
                                            ]}
                                        />
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.withAgent !== curr.withAgent}>
                                        {({ getFieldValue }) =>
                                            getFieldValue(['advertisingInfo', 'ord', 'withAgent']) ? (
                                                <Form.Item
                                                    name={['advertisingInfo', 'ord', 'agentCommission']}
                                                    rules={[{ required: true, message: 'Укажите комиссию агента' }]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        addonAfter="₽"
                                                        placeholder="Сумма комиссии агента"
                                                    />
                                                </Form.Item>
                                            ) : null
                                        }
                                    </Form.Item>
                                    <Row gutter={8} align="bottom">
                                        <Col span={14}>
                                            <Form.Item
                                                noStyle
                                                dependencies={[['advertisingInfo', 'ord', 'resourceUrl']]} // <-- слушаем url
                                            >
                                                {({ getFieldValue }) => {
                                                    const resName =
                                                        getFieldValue(['advertisingInfo', 'ord', 'resourceUrl']) || ''
                                                    return (
                                                        <Form.Item
                                                            label={
                                                                <Link style={{ fontSize: 17 }}>
                                                                    {resName || 'Название площадки'}
                                                                </Link>
                                                            }
                                                            name={['advertisingInfo', 'ord', 'resourceName']}
                                                            rules={[
                                                                { required: true, message: 'Укажи название площадки' }
                                                            ]}
                                                        >
                                                            <Input size="large" placeholder="Название площадки" />
                                                        </Form.Item>
                                                    )
                                                }}
                                            </Form.Item>
                                        </Col>
                                        <Col span={9}>
                                            <Form.Item
                                                label={
                                                    <Text style={{ fontSize: 17, color: colorPrimary }}>
                                                        Тип площадки
                                                    </Text>
                                                }
                                                name={['advertisingInfo', 'ord', 'resourceType']}
                                                valuePropName="checked"
                                                getValueProps={(v) => ({ checked: v === 'site' })} // строка → булево для Switch
                                                getValueFromEvent={(checked: boolean) => (checked ? 'site' : 'app')} // булево → строка в форму
                                            >
                                                <MobileSwitch
                                                    checkedText="Сайт"
                                                    uncheckedText="Приложение"
                                                    alwaysChecked
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Form.Item
                                        label={
                                            <Text style={{ fontSize: '17px', color: colorPrimary }}>
                                                Категория рекламируемого товара или услуги
                                            </Text>
                                        }
                                        className={styles.formItem}
                                        name={['advertisingInfo', 'ord', 'category']}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                    >
                                        <TreeSelect
                                            treeData={transformToTreeData(kktuCodes)}
                                            placeholder="Выберите категорию"
                                            treeExpandAction="click"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item name={['advertisingInfo', 'ord', 'reallocations']} hidden />
                                    {/* <Form.Item
                                        name={['advertisingInfo', 'ord', 'withReallocation']}
                                        label={
                                            <Space>
                                                <Text>Разаллокация</Text>
                                                <Tooltip title="Укажите, какая часть суммы акта/отчёта между исполнителем и заказчиком приходится на интернет-рекламу по атрибутам договора с каждым конечным рекламодателем.">
                                                    <QuestionCircleOutlined />
                                                </Tooltip>
                                            </Space>
                                        }
                                        valuePropName="checked"
                                        wrapperCol={{ span: 'auto ' }}
                                        labelCol={{ span: 'auto ' }}
                                        required
                                    >
                                        <MobileSwitch
                                            onChange={(checked) => {
                                                if (checked) {
                                                    form.setFieldsValue({
                                                        reallocations: [{}]
                                                    })
                                                } else {
                                                    form.setFieldsValue({
                                                        reallocations: []
                                                    })
                                                }
                                            }}
                                        />
                                    </Form.Item> */}

                                    {/* <Form.Item
                                        noStyle
                                        shouldUpdate={(prev, curr) => prev.withReallocation !== curr.withReallocation}
                                    >
                                        {({ getFieldValue }) =>
                                            getFieldValue(['advertisingInfo', 'ord', 'withReallocation']) ? (
                                                <Form.List name={['advertisingInfo', 'ord', 'reallocations']}>
                                                    {(fields, { add, remove }) => (
                                                        <>
                                                            {fields.map(({ key, name, ...restField }) => (
                                                                <div
                                                                    key={key}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'start',
                                                                        gap: 8,
                                                                        marginBottom: 8
                                                                    }}
                                                                >
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'amount']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Введите сумму'
                                                                            }
                                                                        ]}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <InputNumber
                                                                            min={0}
                                                                            placeholder="Сумма, ₽"
                                                                            style={{ width: '100%' }}
                                                                            size="large"
                                                                        />
                                                                    </Form.Item>
                                                                    <Button onClick={() => remove(name)} size="large">
                                                                        <DeleteOutlined />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                            <Button
                                                                type="primary"
                                                                onClick={() => add()}
                                                                size="large"
                                                                style={{ width: '100%' }}
                                                            >
                                                                + Добавить разаллокацию
                                                            </Button>
                                                        </>
                                                    )}
                                                </Form.List>
                                            ) : null
                                        }
                                    </Form.Item> */}
                                </>
                            )}
                        </div>
                    </Col>

                    <Col span={24}>
                        <Flex gap={6}>
                            <Button type="primary" size="large" onClick={onClose}>
                                <CloseOutlined />
                            </Button>
                            <Button
                                type="primary"
                                style={{ flex: 1 }}
                                onClick={() => onSubmit({ state: 'draft' })}
                                loading={editPostIsLoading}
                                size="large"
                            >
                                Сохранить
                                <LockOutlined />
                            </Button>
                        </Flex>
                    </Col>
                </Row>

                <Form.Item<FieldType> name="state" hidden>
                    <Input />
                </Form.Item>
                <Form.Item<FieldType> name="isAdvertising" hidden>
                    <Input />
                </Form.Item>
                <Form.Item<FieldType> name={['advertisingInfo', 'adCampaign']} hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="messages" hidden />
                <Form.Item<FieldType> label="Введите название поста" name="title" hidden initialValue={'Title'}>
                    <Input />
                </Form.Item>
            </Form>
        </Popup>
    )
}
